import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import SignaturePad from '../common/SignaturePad';
import axios from 'axios';

const isSignatureEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

/**
 * SignatureField Component
 *
 * Supports three signature types:
 * - NAME: Simple text input for name (Autocomplete freeSolo when options provided)
 * - DIGITAL: Digital signature using SignaturePad (saved to Signature model)
 * - IMAGE: Image upload (saved to Media model)
 *
 * @param {Object} props
 * @param {String} props.label - Field label
 * @param {String|ObjectId} props.value - Current signature value
 * @param {String} props.signatureType - Current signature type ('NAME', 'DIGITAL', 'IMAGE')
 * @param {Function} props.onChange - Callback when signature changes: (value, signatureType) => void
 * @param {Boolean} props.disabled - Whether field is disabled
 * @param {String} props.helperText - Helper text to display
 * @param {String} props.entityId - Optional entity ID for creating Signature/Media records
 * @param {String} props.entityType - Optional entity type (default: 'TECHNICAL_LOG_BOOK_SIGNATURE')
 * @param {String[]} props.namePresetOptions - When signature type is NAME, optional quick-pick names (e.g. PIC / co-pilot)
 * @param {String[]} props.ameNameOptions - Previous AME names for freeSolo Autocomplete (NAME type)
 * @param {Object|null} props.ameSignaturePreset - Legacy single AME signature preset (prefer ameSignaturePresets)
 * @param {Object[]} props.ameSignaturePresets - Other AME signatures on the sector for reuse: { label, sectionLabel, value, signatureType }
 */
const SignatureField = ({
  label = 'Signature',
  value = '',
  signatureType = 'NAME',
  onChange,
  disabled = false,
  helperText,
  entityId = null,
  entityType = 'TECHNICAL_LOG_BOOK_SIGNATURE',
  namePresetOptions = null,
  ameNameOptions = null,
  ameSignaturePreset = null,
  ameSignaturePresets = null
}) => {
  const [localType, setLocalType] = useState(signatureType || 'NAME');
  const [localValue, setLocalValue] = useState(value || '');
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Update local state when props change
  React.useEffect(() => {
    setLocalType(signatureType || 'NAME');
    setLocalValue(value || '');
  }, [signatureType, value]);

  const handleTypeChange = (newType) => {
    setLocalType(newType);
    // Clear value when switching types
    setLocalValue('');
    if (onChange) {
      onChange('', newType);
    }
  };

  const handleNameChange = (newValue) => {
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue, localType);
    }
  };

  const handleDigitalSignature = async (dataURL) => {
    try {
      setUploading(true);

      // If entityId is provided, create a Signature record
      if (entityId) {
        try {
          const response = await axios.post('/api/signatures', {
            entityType: entityType,
            entityId: entityId,
            imageUrl: dataURL
          });

          setLocalValue(response.data._id);
          setPreviewUrl(dataURL); // Use dataURL for preview
          if (onChange) {
            onChange(response.data._id, 'DIGITAL');
          }
        } catch (apiError) {
          console.error('Error creating signature record:', apiError);
          // Fallback: store base64 directly (backend will convert on save)
          setLocalValue(dataURL);
          setPreviewUrl(dataURL);
          if (onChange) {
            onChange(dataURL, 'DIGITAL');
          }
        }
      } else {
        // No entityId yet - store base64, backend will convert when entry is saved
        setLocalValue(dataURL);
        setPreviewUrl(dataURL);
        if (onChange) {
          onChange(dataURL, 'DIGITAL');
        }
      }

      setSignatureDialogOpen(false);
    } catch (error) {
      console.error('Error saving digital signature:', error);
      alert('Failed to save signature. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      // If entityId is provided, upload via API
      if (entityId) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entityType', entityType);
          formData.append('entityId', entityId);
          formData.append('kind', 'DOC');

          const response = await axios.post('/api/media/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          setLocalValue(response.data._id);
          setPreviewUrl(response.data.fileUrl);
          if (onChange) {
            onChange(response.data._id, 'IMAGE');
          }
          setImageDialogOpen(false);
        } catch (apiError) {
          console.error('Error uploading image via API:', apiError);
          // Fallback: convert to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result;
            setLocalValue(base64String);
            setPreviewUrl(base64String);
            if (onChange) {
              onChange(base64String, 'IMAGE');
            }
            setImageDialogOpen(false);
            setUploading(false);
          };
          reader.onerror = () => {
            alert('Failed to read image file. Please try again.');
            setUploading(false);
          };
          reader.readAsDataURL(file);
          return;
        }
      } else {
        // No entityId yet - convert to base64, backend will convert when entry is saved
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          setLocalValue(base64String);
          setPreviewUrl(base64String);
          if (onChange) {
            onChange(base64String, 'IMAGE');
          }
          setImageDialogOpen(false);
          setUploading(false);
        };
        reader.onerror = () => {
          alert('Failed to read image file. Please try again.');
          setUploading(false);
        };
        reader.readAsDataURL(file);
        return;
      }

      setUploading(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      setUploading(false);
    }
  };

  // Load signature/image preview
  React.useEffect(() => {
    const loadPreview = async () => {
      if (!localValue || localType === 'NAME') {
        setPreviewUrl(null);
        return;
      }

      // Check if it's a base64 data URL or file URL
      if (typeof localValue === 'string') {
        if (localValue.startsWith('data:') || localValue.startsWith('/uploads/')) {
          setPreviewUrl(localValue);
          return;
        }

        // Check if it's a valid ObjectId (24 hex characters)
        const objectIdPattern = /^[0-9a-fA-F]{24}$/;
        if (objectIdPattern.test(localValue)) {
          // It's an ObjectId - fetch from API
          try {
            if (localType === 'DIGITAL') {
              const response = await axios.get(`/api/signatures/${localValue}`);
              setPreviewUrl(response.data.imageUrl);
            } else if (localType === 'IMAGE') {
              const response = await axios.get(`/api/media/${localValue}`);
              setPreviewUrl(response.data.fileUrl);
            }
          } catch (error) {
            console.error('Error loading signature/media:', error);
            // If fetch fails, might be a long base64 string
            if (localValue.length > 100) {
              setPreviewUrl(localValue);
            }
          }
        } else if (localValue.length > 100) {
          // Likely a base64 string without data: prefix
          setPreviewUrl(localValue);
        }
      }
    };

    loadPreview();
  }, [localValue, localType]);

  const presetList = React.useMemo(() => {
    if (!Array.isArray(namePresetOptions)) return [];
    const seen = new Set();
    const out = [];
    for (const raw of namePresetOptions) {
      const s = typeof raw === 'string' ? raw.trim() : '';
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [namePresetOptions]);

  const ameNameList = React.useMemo(() => {
    if (!Array.isArray(ameNameOptions)) return [];
    const seen = new Set();
    const out = [];
    for (const raw of ameNameOptions) {
      const s = typeof raw === 'string' ? raw.trim() : '';
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [ameNameOptions]);

  /** Combined dropdown options for AME NAME fields: previous AME names, then crew presets. */
  const nameAutocompleteOptions = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of [...ameNameList, ...presetList]) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [ameNameList, presetList]);

  const useNameAutocomplete = localType === 'NAME' && (
    Array.isArray(ameNameOptions) || nameAutocompleteOptions.length > 0
  );

  const reusePresets = React.useMemo(() => {
    if (Array.isArray(ameSignaturePresets) && ameSignaturePresets.length > 0) {
      return ameSignaturePresets.filter((p) => p?.value && !isSignatureEmpty(p.value));
    }
    if (ameSignaturePreset?.value && !isSignatureEmpty(ameSignaturePreset.value)) {
      return [ameSignaturePreset];
    }
    return [];
  }, [ameSignaturePresets, ameSignaturePreset]);

  const activeReuseIndex = React.useMemo(() => {
    return reusePresets.findIndex((preset) => {
      const presetType = preset.signatureType || 'NAME';
      if (localType !== presetType) return false;
      if (presetType === 'NAME') {
        return String(localValue || '').trim() === String(preset.value || '').trim();
      }
      return String(localValue || '') === String(preset.value || '');
    });
  }, [reusePresets, localType, localValue]);

  const ameReuseSelectValue = activeReuseIndex >= 0 ? String(activeReuseIndex) : '';

  const applyAmeSignatureReuse = (preset) => {
    if (!preset?.value || !onChange) return;
    const presetType = preset.signatureType || 'NAME';
    setLocalType(presetType);
    setLocalValue(preset.value);
    onChange(preset.value, presetType);
  };

  /** For DIGITAL/IMAGE, offer reuse of other AME signatures on the sector. For NAME, autocomplete covers previous names. */
  const showAmeReuseSelect = localType !== 'NAME' && reusePresets.length > 0;

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Signature Type</InputLabel>
        <Select
          value={localType}
          label="Signature Type"
          onChange={(e) => handleTypeChange(e.target.value)}
          disabled={disabled}
        >
          <MenuItem value="NAME">Name (Text)</MenuItem>
          <MenuItem value="DIGITAL">Digital Signature</MenuItem>
          <MenuItem value="IMAGE">Image Upload</MenuItem>
        </Select>
      </FormControl>

      {showAmeReuseSelect && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>AME signature</InputLabel>
          <Select
            value={ameReuseSelectValue}
            label="AME signature"
            displayEmpty
            onChange={(e) => {
              const idx = e.target.value;
              if (idx === '') return;
              const preset = reusePresets[Number(idx)];
              if (preset) applyAmeSignatureReuse(preset);
            }}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Enter manually</em>
            </MenuItem>
            {reusePresets.map((preset, idx) => (
              <MenuItem key={`${preset.sectionLabel}-${idx}`} value={String(idx)}>
                {preset.sectionLabel
                  ? `Use AME from ${preset.sectionLabel}: ${preset.label}`
                  : `Use AME: ${preset.label}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {localType === 'NAME' && (
        <>
          {useNameAutocomplete ? (
            <Autocomplete
              freeSolo
              options={nameAutocompleteOptions}
              value={localValue || ''}
              onChange={(event, newValue) => {
                handleNameChange(typeof newValue === 'string' ? newValue : '');
              }}
              inputValue={localValue || ''}
              onInputChange={(event, newInputValue) => {
                handleNameChange(newInputValue);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={
                    ameNameList.length > 0
                      ? 'Select previous AME name or type'
                      : presetList.length > 0
                        ? 'Select from crew or type'
                        : 'Type AME name'
                  }
                  helperText={helperText}
                  fullWidth
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              label={label}
              value={localValue}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={disabled}
              helperText={helperText}
            />
          )}
        </>
      )}

      {localType === 'DIGITAL' && (
        <Box>
          {localValue ? (
            <Paper sx={{ p: 2, mb: 2, textAlign: 'center' }}>
              {previewUrl ? (
                <Box>
                  <img
                    src={previewUrl}
                    alt="Signature"
                    style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setSignatureDialogOpen(true)}
                      disabled={disabled}
                    >
                      Re-sign
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Signature saved (ID: {localValue})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setSignatureDialogOpen(true)}
                    disabled={disabled}
                  >
                    Re-sign
                  </Button>
                </Box>
              )}
            </Paper>
          ) : (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<EditIcon />}
              onClick={() => setSignatureDialogOpen(true)}
              disabled={disabled}
            >
              Sign Here
            </Button>
          )}
        </Box>
      )}

      {localType === 'IMAGE' && (
        <Box>
          {localValue ? (
            <Paper sx={{ p: 2, mb: 2, textAlign: 'center' }}>
              {previewUrl ? (
                <Box>
                  <img
                    src={previewUrl}
                    alt="Signature Image"
                    style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setImageDialogOpen(true)}
                      disabled={disabled}
                    >
                      Change Image
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Image uploaded (ID: {localValue})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setImageDialogOpen(true)}
                    disabled={disabled}
                  >
                    Change Image
                  </Button>
                </Box>
              )}
            </Paper>
          ) : (
            <Button
              variant="outlined"
              fullWidth
              component="label"
              startIcon={<ImageIcon />}
              disabled={disabled}
            >
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          )}
        </Box>
      )}

      {/* Digital Signature Dialog */}
      <Dialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Digital Signature</DialogTitle>
        <DialogContent>
          <SignaturePad onSign={handleDigitalSignature} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignatureDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Signature Image</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              id="signature-image-upload"
            />
            <label htmlFor="signature-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<ImageIcon />}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Choose Image File'}
              </Button>
            </label>
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '300px', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignatureField;
